import { jsx, type JsxElementProps } from '@amazon/vinyl-tsx'
import { Icon } from '@/components/Icon'
import { Link } from '@/components/Link'
import { notSignedIn } from '@/auth/authRef'

export interface SignedInRequiredProps extends JsxElementProps<'div'> {
    /**
     * Message shown above the Home link. Defaults to a generic copy
     * suitable for library / personal pages.
     */
    readonly message?: string
}

/**
 * Renders an empty-state panel when the current session isn't
 * signed in. Mirrors the now-playing "Nothing's playing right now"
 * treatment: a single message + a Home button. Wrap a personal /
 * gated page surface like:
 *
 *   <div visible={requiresSignIn$}>
 *     <SignedInRequired />
 *   </div>
 *
 * The `requiresSignIn$` observable is exported for convenience as
 * `signedInRequired$`; the component renders nothing while signed
 * in, so it can also be dropped at the page root and gated on its
 * own.
 */
export function SignedInRequired(props?: SignedInRequiredProps | null) {
    // vinyl-tsx passes `null` (not `undefined`) when a component is
    // used with no attributes — `<SignedInRequired />` shows up as
    // `type(null, [])`. JS default-param assignment only kicks in
    // for `undefined`, so the previous `props = {}` signature still
    // crashed on `props.message`. Coalesce explicitly.
    const { message = "You're signed out. Sign in to see your library.", ...rest } = props ?? {}
    return (
        <div
            {...rest}
            className={`signedInRequired ${rest.className ?? ''}`}
            visible={notSignedIn()}
        >
            <p className="signedInRequiredMessage">{message}</p>
            <Link
                role="button"
                tabIndex={0}
                href="/"
                className="textButton signedInRequiredHomeBtn"
                navigateOptions={{ scroll: 'restore' }}
            >
                <Icon icon="homeFilled" />
                <span className="label">HOME</span>
            </Link>
        </div>
    )
}
